import React, { Component } from 'react';
import classNames from 'classnames';
import { flowRight } from 'lodash';

import { type WithTranslation } from '@wix/yoshi-flow-editor';
import withTranslate from '../../hoc/with-translate';
import { handleEnterKeyUp } from '../../services/accessibility';
import { type Actions, type AppState } from '../../types';
import { SearchIcon } from '../icons/search-icon';
import { connect } from '../runtime-context';
import ClearIcon from './clear-icon';
import styles from './search-input.scss';

type OwnProps = {
  value?: string;
  isExpanded?: boolean;
  isFullWidth?: boolean;
  onChange?: (value: string) => void;
  post?: unknown;
  inputClassName?: string;
  iconClassName?: string;
  borderClassName?: string;
  className?: string;
  type?: string;
  color?: string;
};

type Props = OwnProps &
  Partial<Pick<ReturnType<typeof mapRuntimeToProps>, 'headerSearchOpened' | 'headerSearchClosed'>> &
  Omit<ReturnType<typeof mapRuntimeToProps>, 'headerSearchOpened' | 'headerSearchClosed'> &
  Pick<WithTranslation, 't'>;

type State = {
  value: string;
  isExpanded?: boolean;
};

export class SearchInput extends Component<Props, State> {
  input: HTMLInputElement | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      value: props.value || '',
      isExpanded: props.isExpanded,
    };

    this.setInput = this.setInput.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  setInput(node: HTMLInputElement | null) {
    this.input = node;
  }

  isExpandable() {
    return !this.props.isExpanded;
  }

  expand() {
    this.setState({ isExpanded: true });
    this.props.headerSearchOpened?.();
  }

  collapse() {
    this.setState({ isExpanded: false });
    this.props.headerSearchClosed?.();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (!prevState.isExpanded && this.state.isExpanded) {
      this.input?.focus();
    }
  }

  goToSearchPage = () => {
    this.props.setIsLoading('search', '', true);
    this.props.searchSubmitted({
      query: this.state.value,
      post: this.props.post,
    });
    this.props.navigateToSearch(this.state.value);
  };

  handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    this.setValue(e.target.value);
  }

  handleClear() {
    this.setValue('');
    if (this.isExpandable()) {
      this.collapse();
    }
  }

  handleClick() {
    if (this.isExpandable() && !this.state.isExpanded) {
      this.expand();
    } else {
      this.goToSearchPage();
    }
  }

  handleKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      this.goToSearchPage();
    } else if (event.key === 'Escape') {
      (event.target as HTMLInputElement).blur();
      this.handleClear();
    }
  }

  setValue(value: string) {
    this.setState({ value }, () => {
      const { onChange } = this.props;
      if (onChange) {
        onChange(this.state.value);
      }
    });
  }

  render() {
    const {
      type,
      className,
      inputClassName,
      iconClassName,
      borderClassName,
      color,
      t,
      isFullWidth,
    } = this.props;
    const { isExpanded, value } = this.state;
    const showClearButton = (this.isExpandable() && isExpanded) || (!this.isExpandable() && value);
    const containerClassName = classNames(
      styles.container,
      className,
      'search-input',
      type && styles[type],
      {
        [styles.expanded]: isExpanded,
        [styles.isFullWidth]: isFullWidth,
      },
    );

    return (
      <div className={containerClassName} tabIndex={-1}>
        <div
          tabIndex={!isExpanded ? 0 : undefined}
          role="button"
          aria-label={t('search-input.placeholder')}
          onKeyUp={handleEnterKeyUp(this.handleClick)}
          onClick={this.handleClick}
        >
          <SearchIcon className={classNames(styles.icon, iconClassName)} fill={color} />
        </div>
        <input
          ref={this.setInput}
          className={classNames(styles.input, inputClassName, 'search-input__input')}
          onChange={this.handleChange}
          value={value}
          type="text"
          placeholder={t('search-input.placeholder')}
          onKeyUp={this.handleKeyUp}
          style={{ color }}
        />
        <div className={classNames(styles.spacer, inputClassName, styles.placeholder)}>
          {t('search-input.placeholder')}
        </div>
        <div
          className={classNames(styles.border, borderClassName)}
          style={{ backgroundColor: color }}
        />
        {showClearButton ? (
          <button
            className={classNames(styles.clear, 'search-input__clear-button')}
            onClick={this.handleClear}
            tabIndex={-1}
            data-hook="clear-button"
          >
            <ClearIcon className={classNames(styles.clearIcon, iconClassName)} fill={color} />
          </button>
        ) : null}
      </div>
    );
  }
}

const mapRuntimeToProps = (_state: AppState, _ownProps: {}, actions: Actions) => ({
  headerSearchOpened: actions.headerSearchOpened,
  headerSearchClosed: actions.headerSearchClosed,
  navigateToSearch: actions.navigateToSearch,
  setIsLoading: actions.setIsLoading,
  searchSubmitted: actions.searchSubmitted,
});

export default flowRight(connect(mapRuntimeToProps), withTranslate)(SearchInput);
